import { FragmentFilter, open } from "@fastpubsub-sdk/client";
import {
  formatLinkQuality,
  FPS_LINK_REFRESH_MS,
  readLinkQuality,
  withWsLinkQuality
} from "../shared/fps-connection.js";
import { scheduleSoftTokenRefresh } from "../shared/token-lifetime.js";
import { HIGH_VIDEO_MODES, LOW_VIDEO_MODES } from "./video-codecs.js";
import { createAudioDiagnostics, isSignificantDiagnosticEvent, recordAudioDiagnostic as applyAudioDiagnostic } from "./audio-diagnostics.js";
import { AudioPublisher, AudioReceiver } from "./audio-pipeline.js";
import { ConferenceAudioMixer } from "./audio-mixer.js";
import { DesktopPublisher } from "./desktop-publisher.js";
import { captureDisplayMedia } from "./display-media.js";
import { DEFAULT_MIC_DEVICE_ID, microphoneConstraints } from "./media-devices.js";
import { ParticipantStateHub } from "./participant-state-hub.js";
import { FastPubSubAudioTransport } from "./fastpubsub-audio.js";
import { FastPubSubVideoTransport } from "./fastpubsub-video.js";
import { VideoPublisher, VideoReceiver } from "./video-pipeline.js";

/**
 * Coordinates one conference session.
 *
 * It is intentionally independent of Preact components. The `ui` argument is
 * a tiny view contract implemented by `PreactRoomUI`; that keeps API and media
 * workflows readable and testable without coupling them to DOM queries.
 */
export class ConferenceController {
  constructor({ ui, chat }) {
    this.ui = ui;
    this.chat = chat;

    // View settings, not DOM elements. A future UI can reuse this controller.
    this.highMode = "auto";
    this.lowMode = "auto";
    /** Чей high смотрим: solo/fullscreen. Пусто = все плитки low. */
    this.viewHighParticipantId = "";
    this.highStopTimer = undefined;
    this.participantMedia = new Map();
    this.audioDiagnostics = createAudioDiagnostics();
    this.microphoneSpeaking = false;
    this.reconcileMediaFrame = undefined;
    /** Empty string — system default microphone. */
    this.selectedMicId = DEFAULT_MIC_DEVICE_ID;
    /** Preferred video source: camera or desktop (not both at once). */
    this.preferredVideoSource = "camera";
    /** Active published video source: none, camera, or desktop. */
    this.activeVideoSource = "none";
    this.edgeLabel = "";
    this.linkQualityLabel = "ws rtt …";
    this.linkQualityTimer = undefined;
    
    this.selectedEdgeApiBase = "";
    this.tokenRefreshHandle = null;
  }

  /**
   * Слушатель просит high этого участника (solo или fullscreen).
   * Плитки сетки — всегда low.
   * @param {string} participantId
   */
  setVideoViewDemand(participantId) {
    const selfId = this.joined?.participant?.participant_id;
    const id = participantId && participantId !== selfId ? participantId : "";
    if (this.viewHighParticipantId === id) return;
    this.viewHighParticipantId = id;
    this.participantStateHub?.setLocal({ wantVideoHigh: id });
    this.scheduleReconcileMediaSubscriptions();
  }

  async enableRemoteAudio() {
    const state = await this.audioMixer.enable();
    const enabled = state === "running";
    this.ui.setAudioEnabled(enabled);
    if (enabled) this.disarmAudioPlayback();
    if (!enabled) this.ui.setMediaNote("Audio playback is ready; the browser will unlock it on your next click or key press.");
    return enabled;
  }

  setParticipantVolume(participantId, volume) {
    const policy = this.policyFor(participantId);
    policy.volume = volume;
    this.audioMixer.setVolume(this.audioTransport.audioChannel(this.privatePrefixForId(participantId)), volume);
    this.ui.setParticipantMedia(participantId, { volume });
  }

  setParticipantMuted(participantId, muted) {
    const policy = this.policyFor(participantId);
    policy.muted = muted;
    this.audioMixer.setMuted(this.audioTransport.audioChannel(this.privatePrefixForId(participantId)), muted);
    this.ui.setParticipantMedia(participantId, { muted });
  }

  policyFor(participantId) {
    let policy = this.participantMedia.get(participantId);
    if (!policy) {
      policy = { volume: 1, muted: false };
      this.participantMedia.set(participantId, policy);
    }
    return policy;
  }

  async setVideoModes({ highMode, lowMode }) {
    this.highMode = highMode;
    this.lowMode = lowMode;
    await this.restartVideoPublishing();
  }

  async createConference({ title, chatPolicy, displayName }) {
    this.ui.setStatus("creating meeting…", true);
    const response = await fetch("/api/v1/demo/conference/conferences", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ title, chat_policy: chatPolicy })
    });
    if (!response.ok) throw new Error(await response.text());

    const created = await response.json();
    this.ui.showLinks(created);
    await this.connect("admin", {
      conferenceId: created.conference_id,
      ownerSecret: created.owner_secret,
      displayName
    });
  }

  async joinConference(kind, details) {
    await this.connect(kind, details);
  }

  /** Bootstrap FastPubSub, acquire a scoped token, then enter the room. */
  async connect(kind, details) {
    this.ui.setStatus("bootstrap: choosing nearest edge…", true);

    const configResponse = await fetch("/api/v1/demo/conference/config");
    if (!configResponse.ok) throw new Error(`conference config ${configResponse.status}`);

    const config = await configResponse.json();
    this.session = open(config.overlay);
    const edge = await this.session.resolveEdge({ cache: "prefer" });
    this.selectedEdgeApiBase = edge.candidate.apiBase;

    const endpoint = kind === "admin"
      ? `/api/v1/demo/conference/conferences/${encodeURIComponent(details.conferenceId)}/admin/join`
      : `/api/v1/demo/conference/invites/${encodeURIComponent(details.inviteId)}/join`;
    const body = { api_base: edge.candidate.apiBase, display_name: details.displayName };
    if (kind === "admin") body.owner_secret = details.ownerSecret;

    const response = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body)
    });
    if (!response.ok) throw new Error(await response.text());

    this.joined = await response.json();
    this.ownerSecret = kind === "admin" ? details.ownerSecret : undefined;
    this.roomClosed = false;
    this.roomState = this.joined.conference;
    this.resetAudioDiagnostics();
    this.client = await withWsLinkQuality(this.session
      .webSocket(this.joined.token)
      .addFilter(this.joined.tenant, "private.", new FragmentFilter(48 * 1024, 16 * 1024))
      .filterTimerMode("lowLatency200Hz"))
      .build();

    this.initialiseVideoStack();
    this.participantStateHub = new ParticipantStateHub({
      client: this.client,
      tenant: this.joined.tenant,
      channelRole: this.channelRole(this.joined.participant),
      participantId: this.joined.participant.participant_id,
      onRemoteUpdate: () => this.onParticipantStateUpdate(),
      onRoomClosed: () => this.onConferenceClosedByHost(),
      onWarning: (message, error) => console.warn(message, error)
    });
    this.participantStateHub.start();
    this.armAudioPlayback();
    void this.enableRemoteAudio().catch(error => {
      this.ui.setAudioEnabled(false);
      this.ui.setMediaNote(`Audio playback waits for browser permission: ${friendlyError(error)}`);
    });
    this.chat.start(this.client, this.joined.tenant, this.chatTransport());
    void this.startAuthoritySubscription();
    

    this.ui.showConference(this.joined.conference, this.joined.media_publish_allowed);
    this.renderRoom(this.roomState);
    this.edgeLabel = `${edge.candidate.id} (edge ${Math.round(edge.pingMs)} ms)`;
    this.startLinkQualityTimer();
    this.armSoftTokenRefresh(this.joined.expires_in_sec);
    this.refreshConnectionStatus();
    this.roomUiTimer = window.setInterval(() => this.refreshRoomUi(), 1000);
  }

  /**
   * Soft-extends meeting AT via demo-server; keeps the open WebSocket.
   * @param {number} expiresInSec
   */
  armSoftTokenRefresh(expiresInSec) {
    this.tokenRefreshHandle?.clear();
    if (!expiresInSec || !this.joined?.token) {
      return;
    }
    this.tokenRefreshHandle = scheduleSoftTokenRefresh({
      expiresInSec,
      refresh: async () => {
        const response = await fetch("/api/v1/demo/conference/token/refresh", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            api_base: this.selectedEdgeApiBase,
            token: this.joined.token
          })
        });
        if (!response.ok) {
          throw new Error(await response.text());
        }
        const body = await response.json();
        this.joined.token = body.token;
        this.joined.expires_in_sec = body.expires_in_sec;
        return body;
      },
      onSoftOk: () => {
        console.info("[conference] AT soft refreshed");
      },
      onError: (error) => {
        console.warn("[conference] AT soft refresh failed", error);
        this.ui.setStatus(`token refresh failed: ${error.message}`, false);
      }
    });
  }

  /** Polls WS link quality and refreshes the header status every 3 seconds. */
  startLinkQualityTimer() {
    if (this.linkQualityTimer) {
      window.clearInterval(this.linkQualityTimer);
    }
    const tick = () => {
      if (!this.client || this.roomClosed) {
        return;
      }
      this.linkQualityLabel = formatLinkQuality(readLinkQuality(this.client));
      this.refreshConnectionStatus();
    };
    tick();
    this.linkQualityTimer = window.setInterval(tick, FPS_LINK_REFRESH_MS);
  }

  refreshConnectionStatus() {
    if (!this.edgeLabel || this.roomClosed) {
      return;
    }
    this.ui.setStatus(
      `connected · ${this.edgeLabel} · ${this.linkQualityLabel}`,
      true
    );
  }

  initialiseVideoStack() {
    this.videoReceiver = new VideoReceiver({
      findTarget: channel => this.ui.remoteVideoTarget(channel),
      canDraw: participantId => this.participantCameraOn(participantId),
      onWarning: console.warn
    });
    this.audioMixer = new ConferenceAudioMixer({
      onState: ({ label }) => this.ui.setMediaNote(label),
      onAudioDiagnostic: event => this.recordAudioDiagnostic(event),
      onWarning: console.warn
    });
    this.audioReceiver = new AudioReceiver({
      onAudioData: (channel, audioData) => { void this.audioMixer.play(channel, audioData); },
      onAudioDiagnostic: event => this.recordAudioDiagnostic(event),
      onWarning: console.warn
    });
    this.videoTransport = new FastPubSubVideoTransport({
      client: this.client,
      tenant: this.joined.tenant,
      subjectPrefix: this.privatePrefix(this.joined.participant),
      onVideoPacket: (channel, payload) => this.videoReceiver.receive(channel, payload),
      onWarning: console.warn
    });
    this.audioTransport = new FastPubSubAudioTransport({
      client: this.client,
      tenant: this.joined.tenant,
      subjectPrefix: this.privatePrefix(this.joined.participant),
      onAudioPacket: (channel, payload) => this.audioReceiver.receive(channel, payload),
      onAudioDiagnostic: event => this.recordAudioDiagnostic(event),
      onWarning: console.warn
    });
    this.videoPublisher = new VideoPublisher({
      publishPacket: (quality, payload) => this.videoTransport.publishVideo(quality, payload),
      onError: (error, quality) => {
        console.error("[conference] video pipeline failed", { quality, error });
        this.ui.setStatus(`camera error: ${friendlyError(error)}`, false);
      },
      onState: ({ labels }) => this.ui.setMediaNote(`Publishing ${labels.join(" · ")} through FastPubSub.`)
    });
    this.desktopPublisher = new DesktopPublisher({
      publishPacket: (quality, payload) => this.videoTransport.publishVideo(quality, payload),
      onError: error => {
        console.error("[conference] desktop share failed", error);
        this.ui.setStatus(`desktop error: ${friendlyError(error)}`, false);
      },
      onState: ({ label }) => this.ui.setMediaNote(`Publishing ${label} through FastPubSub.`),
      onEnded: () => {
        if (this.activeVideoSource === "desktop") {
          this.stopDesktop();
        }
      }
    });
    this.audioPublisher = new AudioPublisher({
      publishPacket: payload => this.audioTransport.publishAudio(payload),
      voiceActivated: false,
      onSpeaking: speaking => {
        this.microphoneSpeaking = speaking;
        this.ui.setMicrophoneSpeaking(speaking);
        this.participantStateHub?.setLocal({ speaking });
      },
      onDiagnostic: event => this.recordAudioDiagnostic(event),
      onError: error => this.ui.setStatus(`microphone error: ${friendlyError(error)}`, false),
      onState: ({ label }) => this.ui.setMediaNote(`Publishing camera + ${label} through FastPubSub.`)
    });
  }

  renderRoom(snapshot) {
    this.roomState = snapshot;
    if (snapshot.closed) {
      this.onConferenceClosedByHost();
      return;
    }
    this.applyRoomToUi();
    this.scheduleReconcileMediaSubscriptions();
  }

  /** Хост закрыл комнату: стоп медиа и на стартовый экран /demo/conference. */
  onConferenceClosedByHost() {
    if (this.roomClosed) return;
    this.stopMediaForClosedRoom();
    this.ui.exitRoom("conference closed by its owner");
  }

  /** Room for UI: online/offline from public.*.state heartbeats, not HTTP. */
  buildRoomViewForUi() {
    const participants = {};
    for (const [participantId, participant] of Object.entries(this.roomState.participants)) {
      participants[participantId] = {
        ...participant,
        status: this.participantPresenceOnline(participantId) ? "online" : "offline"
      };
    }
    for (const participantId of this.participantStateHub?.onlineParticipantIds() ?? []) {
      if (participants[participantId]) continue;
      const state = this.participantStateHub.remoteState(participantId);
      participants[participantId] = {
        participant_id: participantId,
        display_name: participantId,
        role: state.channelRole || "participant",
        status: "online"
      };
    }
    return { ...this.roomState, participants };
  }

  /** Participant online: local while in session; remote via public.*.state at 1 Hz. */
  participantPresenceOnline(participantId) {
    if (participantId === this.joined.participant.participant_id) {
      return !this.roomClosed;
    }
    return this.participantStateHub?.participantOnline(participantId) ?? false;
  }

  applyRoomToUi() {
    this.ui.renderRoom(this.buildRoomViewForUi(), this.joined.participant.participant_id);
    this.refreshParticipantStateUi();
  }

  /** Once per second: public.*.state staleness and UI refresh. */
  refreshRoomUi() {
    this.refreshParticipantStateUi();
    if (!this.roomState || this.roomClosed) return;
    this.ui.renderRoom(this.buildRoomViewForUi(), this.joined.participant.participant_id);
    this.scheduleReconcileMediaSubscriptions();
    void this.syncHighPublishing();
  }

  onParticipantStateUpdate() {
    if (!this.roomState || this.roomClosed) return;
    this.ui.renderRoom(this.buildRoomViewForUi(), this.joined.participant.participant_id);
    this.refreshParticipantStateUi();
    this.scheduleReconcileMediaSubscriptions();
    void this.syncHighPublishing();
  }

  /** Waits for Preact to mount tiles before subscribing to remote video. */
  scheduleReconcileMediaSubscriptions() {
    if (this.reconcileMediaFrame) return;
    this.reconcileMediaFrame = window.requestAnimationFrame(() => {
      this.reconcileMediaFrame = undefined;
      this.reconcileMediaSubscriptions();
    });
  }

  stopMediaForClosedRoom() {
    if (this.roomClosed) return;
    this.roomClosed = true;
    if (this.highStopTimer) {
      window.clearTimeout(this.highStopTimer);
      this.highStopTimer = undefined;
    }
    this.videoPublisher?.stop();
    this.desktopPublisher?.stop();
    this.audioPublisher?.stop();
    this.mediaStream?.getTracks().forEach(track => track.stop());
    this.mediaStream = undefined;
    this.videoReceiver?.closeAll();
    this.audioReceiver?.closeAll();
    this.videoTransport?.stop();
    this.audioTransport?.stop();
    this.audioMixer?.close();
    this.disarmAudioPlayback();
    this.chat.stop();
    this.participantStateHub?.setOffline();
    this.participantStateHub?.stop();
    if (this.reconcileMediaFrame) {
      window.cancelAnimationFrame(this.reconcileMediaFrame);
      this.reconcileMediaFrame = undefined;
    }
    window.clearInterval(this.roomUiTimer);
    window.clearInterval(this.linkQualityTimer);
    this.linkQualityTimer = undefined;
    this.tokenRefreshHandle?.clear();
    this.tokenRefreshHandle = null;
    this.client?.close();
    this.client = undefined;
  }

  /** Updates participant state panel and video placeholders. */
  refreshParticipantStateUi() {
    if (!this.participantStateHub) return;
    const participantState = this.buildParticipantStateView();
    this.ui.setParticipantState(participantState);
    this.ui.syncParticipantVideoPlaceholders(participantState);
  }

  /** Local + remote state for UI. */
  buildParticipantStateView() {
    const participantState = this.participantStateHub.snapshot();
    participantState[this.joined.participant.participant_id] = {
      camera: this.hasLiveTrack("video"),
      microphone: this.hasLiveTrack("audio"),
      speaking: this.microphoneSpeaking,
      videoSource: this.activeVideoSource,
      stale: false,
      ageMs: 0,
      local: true
    };
    return participantState;
  }

  /** Camera on per public.*.state (1 Hz), not HTTP authority. */
  participantCameraOn(participantId) {
    if (participantId === this.joined.participant.participant_id) {
      return this.hasLiveTrack("video");
    }
    return this.participantStateHub?.participantCameraOn(participantId) ?? false;
  }

  /** Microphone on per public.*.state. */
  participantMicrophoneOn(participantId) {
    if (participantId === this.joined.participant.participant_id) {
      return this.hasLiveTrack("audio");
    }
    return this.participantStateHub?.participantMicrophoneOn(participantId) ?? false;
  }

  /** Качество подписки: плитки low, solo/fullscreen high, desktop всегда high. */
  remoteVideoQuality(participantId) {
    if (participantId === this.joined.participant.participant_id) {
      return this.activeVideoSource === "desktop" ? "high" : "low";
    }
    if (this.participantStateHub?.participantVideoSource(participantId) === "desktop") {
      return "high";
    }
    if (this.viewHighParticipantId === participantId) return "high";
    return "low";
  }

  /** Subscribe to video/audio for participants seen alive on public.*.state. */
  reconcileMediaSubscriptions() {
    if ((!this.videoTransport && !this.audioTransport) || !this.roomState || this.roomClosed) return;

    const wantedChannels = new Set();
    for (const participantId of this.participantStateHub?.onlineParticipantIds() ?? []) {
      const prefix = this.participantStateHub.privatePrefixFor(participantId);
      if (!prefix || !this.remoteCanPublishMedia(participantId, prefix)) continue;
      const quality = this.remoteVideoQuality(participantId);
      if (this.participantVideoOn(participantId) && quality !== "off") {
        wantedChannels.add(this.videoTransport.videoChannel(prefix, quality));
      }
    }

    const staleVideoChannels = [];
    for (const channel of this.videoTransport.subscriptions.keys()) {
      if (!wantedChannels.has(channel)) staleVideoChannels.push(channel);
    }
    this.videoTransport.syncSubscriptions(wantedChannels);
    for (const channel of staleVideoChannels) this.videoReceiver.close(channel);
    for (const channel of [...this.videoReceiver.tracks.keys()]) {
      if (!wantedChannels.has(channel)) this.videoReceiver.close(channel);
    }

    const audioPrefixes = (this.participantStateHub?.onlineParticipantIds() ?? [])
      .map(participantId => ({
        participantId,
        prefix: this.participantStateHub.privatePrefixFor(participantId)
      }))
      .filter(item => item.prefix
        && this.remoteCanPublishMedia(item.participantId, item.prefix)
        && this.participantMicrophoneOn(item.participantId))
      .map(item => item.prefix);

    for (const channel of this.audioTransport.subscriptions.keys()) {
      if (!audioPrefixes.some(prefix => channel === this.audioTransport.audioChannel(prefix))) {
        this.audioReceiver.close(channel);
        this.audioMixer.remove(channel);
      }
    }
    this.audioTransport.syncSubscriptions(audioPrefixes);
  }

  async toggleCamera() {
    await this.toggleVideo();
  }

  /**
   * Select video source. Stops publishing if something is already live.
   * @param {"camera"|"desktop"} source
   */
  setPreferredVideoSource(source) {
    if (source !== "camera" && source !== "desktop") {
      return;
    }
    if (this.preferredVideoSource === source) {
      return;
    }
    this.preferredVideoSource = source;
    this.ui.setPreferredVideoSource(source);
    if (this.hasLiveTrack("video")) {
      this.stopVideo();
    }
  }

  async toggleVideo() {
    if (this.hasLiveTrack("video")) {
      this.stopVideo();
      return;
    }
    if (this.preferredVideoSource === "desktop") {
      await this.startDesktop();
    } else {
      await this.startCamera();
    }
  }

  stopVideo() {
    if (this.activeVideoSource === "desktop") {
      this.stopDesktop();
    } else if (this.activeVideoSource === "camera") {
      this.stopCamera();
    }
  }

  participantVideoOn(participantId) {
    if (participantId === this.joined.participant.participant_id) {
      return this.hasLiveTrack("video");
    }
    return this.participantStateHub?.participantVideoOn(participantId) ?? false;
  }

  async toggleMicrophone() {
    if (this.hasLiveTrack("audio")) this.stopMicrophone();
    else await this.startMicrophone();
  }

  /**
   * Select microphone. Empty deviceId restores the system default.
   * Restarts capture when the microphone is already enabled.
   * @param {string} deviceId
   */
  async setMicrophoneDevice(deviceId) {
    const nextId = deviceId || DEFAULT_MIC_DEVICE_ID;
    if (nextId === this.selectedMicId) {
      return;
    }
    this.selectedMicId = nextId;
    if (this.hasLiveTrack("audio")) {
      this.stopMicrophone();
      await this.startMicrophone();
    }
  }

  async startCamera() {
    try {
      this.stopVideo();
      if (!navigator.mediaDevices?.getUserMedia) {
        const reason = window.isSecureContext
          ? "Browser does not provide the MediaDevices camera API."
          : "Camera access requires HTTPS (or http://localhost).";
        throw new Error(reason);
      }
      if (!globalThis.VideoEncoder || !globalThis.VideoDecoder) {
        throw new Error("This browser does not support WebCodecs video.");
      }

      const capture = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          frameRate: { ideal: 30 }
        },
        audio: false
      });
      this.addTracks(capture.getVideoTracks());

      const preview = await this.ui.showLocalStream(this.mediaStream);
      await this.startVideoPublishing(preview);
      this.activeVideoSource = "camera";
      this.ui.setVideoPublishing(true, "camera");
      this.participantStateHub?.setLocal({ camera: true, videoSource: "camera" });
    } catch (error) {
      this.videoPublisher?.stop();
      this.stopTracks("video");
      this.ui.clearLocalStream();
      this.activeVideoSource = "none";
      this.ui.setVideoPublishing(false, "none");
      this.participantStateHub?.setLocal({ camera: false, videoSource: "none" });
      this.ui.setStatus(`camera error: ${friendlyError(error)}`, false);
    }
  }

  stopCamera() {
    this.videoPublisher?.stop();
    this.stopTracks("video");
    this.ui.clearLocalStream();
    this.activeVideoSource = "none";
    this.ui.setVideoPublishing(false, "none");
    this.participantStateHub?.setLocal({ camera: false, videoSource: "none" });
    this.scheduleReconcileMediaSubscriptions();
  }

  async startDesktop() {
    try {
      this.stopVideo();
      if (!globalThis.VideoEncoder || !globalThis.VideoDecoder) {
        throw new Error("This browser does not support WebCodecs video.");
      }

      const capture = await captureDisplayMedia();
      this.addTracks(capture.getVideoTracks());

      const preview = await this.ui.showLocalStream(this.mediaStream);
      await this.desktopPublisher.start({ mediaStream: this.mediaStream, preview });
      this.activeVideoSource = "desktop";
      this.ui.setVideoPublishing(true, "desktop");
      this.participantStateHub?.setLocal({ camera: true, videoSource: "desktop" });
    } catch (error) {
      this.desktopPublisher?.stop();
      this.stopTracks("video");
      this.ui.clearLocalStream();
      this.activeVideoSource = "none";
      this.ui.setVideoPublishing(false, "none");
      this.participantStateHub?.setLocal({ camera: false, videoSource: "none" });
      if (error?.name !== "NotAllowedError") {
        this.ui.setStatus(`desktop error: ${friendlyError(error)}`, false);
      }
    }
  }

  stopDesktop() {
    this.desktopPublisher?.stop();
    this.stopTracks("video");
    this.ui.clearLocalStream();
    this.activeVideoSource = "none";
    this.ui.setVideoPublishing(false, "none");
    this.participantStateHub?.setLocal({ camera: false, videoSource: "none" });
    this.scheduleReconcileMediaSubscriptions();
  }

  async startMicrophone() {
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error("Microphone access requires HTTPS and the MediaDevices API.");
      }
      await this.enableRemoteAudio();
      const capture = await navigator.mediaDevices.getUserMedia(
        microphoneConstraints(this.selectedMicId)
      );
      this.addTracks(capture.getAudioTracks());
      await this.audioPublisher.start(this.mediaStream);
      this.ui.setMicrophoneEnabled(true);
      this.microphoneSpeaking = false;
      this.ui.setMicrophoneSpeaking(false);
      this.participantStateHub?.setLocal({ microphone: true, speaking: false });
    } catch (error) {
      this.audioPublisher?.stop();
      this.stopTracks("audio");
      this.microphoneSpeaking = false;
      this.ui.setMicrophoneSpeaking(false);
      this.participantStateHub?.setLocal({ microphone: false, speaking: false });
      this.ui.setStatus(`microphone error: ${friendlyError(error)}`, false);
    }
  }

  stopMicrophone() {
    this.audioPublisher?.stop();
    this.stopTracks("audio");
    this.ui.setMicrophoneEnabled(false);
    this.microphoneSpeaking = false;
    this.ui.setMicrophoneSpeaking(false);
    this.participantStateHub?.setLocal({ microphone: false, speaking: false });
    this.scheduleReconcileMediaSubscriptions();
  }

  addTracks(tracks) {
    if (!this.mediaStream) this.mediaStream = new MediaStream();
    for (const track of tracks) this.mediaStream.addTrack(track);
  }

  stopTracks(kind) {
    const tracks = kind === "video" ? this.mediaStream?.getVideoTracks() : this.mediaStream?.getAudioTracks();
    for (const track of tracks || []) {
      track.stop();
      this.mediaStream.removeTrack(track);
    }
    if (!this.mediaStream?.getTracks().length) this.mediaStream = undefined;
  }

  hasLiveTrack(kind) {
    const tracks = kind === "video" ? this.mediaStream?.getVideoTracks() : this.mediaStream?.getAudioTracks();
    return Boolean(tracks?.some(track => track.readyState === "live"));
  }

  async restartVideoPublishing() {
    if (!this.hasLiveTrack("video") || this.activeVideoSource !== "camera") return;

    try {
      this.videoPublisher.stop();
      await this.startVideoPublishing(this.ui.localVideo());
    } catch (error) {
      this.ui.setStatus(`camera error: ${friendlyError(error)}`, false);
    }
  }

  async startVideoPublishing(preview) {
    const lowMode = LOW_VIDEO_MODES[this.lowMode] || LOW_VIDEO_MODES.auto;
    await this.videoPublisher.start({
      mediaStream: this.mediaStream,
      preview,
      modes: [{ quality: "low", ...lowMode }]
    });
    await this.syncHighPublishing();
  }

  /**
   * High-энкодер камеры только если кто-то просит (solo/fullscreen).
   * Выключение с паузой 1.5 с, чтобы не дёргать при коротком клике.
   */
  async syncHighPublishing() {
    if (this.activeVideoSource !== "camera" || !this.videoPublisher || this.roomClosed) return;
    const me = this.joined?.participant?.participant_id;
    const want = Boolean(me && this.participantStateHub?.someoneWantsHigh(me));
    if (want) {
      if (this.highStopTimer) {
        window.clearTimeout(this.highStopTimer);
        this.highStopTimer = undefined;
      }
      if (this.videoPublisher.hasMode("high")) return;
      const highMode = HIGH_VIDEO_MODES[this.highMode] || HIGH_VIDEO_MODES.auto;
      try {
        await this.videoPublisher.addMode({ quality: "high", ...highMode });
      } catch (error) {
        this.ui.setStatus(`camera error: ${friendlyError(error)}`, false);
      }
      return;
    }
    if (!this.videoPublisher.hasMode("high") || this.highStopTimer) return;
    this.highStopTimer = window.setTimeout(() => {
      this.highStopTimer = undefined;
      if (this.participantStateHub?.someoneWantsHigh(this.joined?.participant?.participant_id)) {
        void this.syncHighPublishing();
        return;
      }
      this.videoPublisher?.stopMode("high");
    }, 1500);
  }

  async startAuthoritySubscription() {
    const queue = await this.client.subscribe(this.joined.tenant, "authority.#");
    while (this.client) {
      const message = await queue.recv();
      if (!message) break;

      try {
        const event = JSON.parse(new TextDecoder().decode(message.payload));
        if (event.type === "conference_closed" || event.conference?.closed) {
          this.onConferenceClosedByHost();
          continue;
        }
        if (event.type === "conference_state" && event.conference?.version >= this.roomState.version) {
          this.renderRoom(event.conference);
        }
      } catch {
        // Ignore malformed authority events.
      }
    }
  }

  async sendChat(text) {
    await this.chat.send(this.joined.participant, text);
  }

  async closeConference() {
    if (!this.ownerSecret) throw new Error("Only the conference owner can close this room.");
    const response = await fetch(this.conferencePath("close"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ owner_secret: this.ownerSecret })
    });
    if (!response.ok) throw new Error(await response.text());
    const snapshot = await response.json();
    // Сначала в канал гостям (свой открытый WS), потом себе на стартовый экран.
    try {
      await this.participantStateHub?.publishRoomClosed(snapshot.conference_id);
    } catch (error) {
      console.warn("[conference] room closed publish failed", error);
    }
    this.renderRoom(snapshot);
  }

  leave() {
    if (!this.joined) return;

    if (this.highStopTimer) {
      window.clearTimeout(this.highStopTimer);
      this.highStopTimer = undefined;
    }
    this.videoPublisher?.stop();
    this.desktopPublisher?.stop();
    this.audioPublisher?.stop();
    this.mediaStream?.getTracks().forEach(track => track.stop());
    this.mediaStream = undefined;
    this.videoReceiver?.closeAll();
    this.audioReceiver?.closeAll();
    this.videoTransport?.stop();
    this.audioTransport?.stop();
    this.audioMixer?.close();
    this.disarmAudioPlayback();
    this.chat.stop();
    this.participantStateHub?.setOffline();
    this.participantStateHub?.stop();

    window.clearInterval(this.roomUiTimer);
    window.clearInterval(this.linkQualityTimer);
    this.linkQualityTimer = undefined;
    this.tokenRefreshHandle?.clear();
    this.tokenRefreshHandle = null;
    this.client?.close();
    this.client = undefined;
  }

  conferencePath(suffix) {
    const conferenceId = encodeURIComponent(this.joined.conference.conference_id);
    return `/api/v1/demo/conference/conferences/${conferenceId}/${suffix}`;
  }

  privatePrefixForId(participantId) {
    const participant = this.roomState?.participants?.[participantId];
    if (participant) return this.privatePrefix(participant);
    const fromState = this.participantStateHub?.privatePrefixFor(participantId);
    if (fromState) return fromState;
    throw new Error(`participant ${participantId} is not in the room state`);
  }

  privatePrefix(participant) {
    return `private.${this.channelRole(participant)}.${participant.participant_id}`;
  }

  /** Role segment in subject paths — meeting mode always uses participant. */
  channelRole(_participant) {
    return "participant";
  }

  /** Whether a remote prefix may publish media. */
  remoteCanPublishMedia(participantId, prefix) {
    const role = prefix.split(".")[1];
    if (role !== "participant") {
      return false;
    }
    const participant = this.roomState.participants[participantId];
    if (participant) {
      return this.participantCanPublishMedia(participant);
    }
    return true;
  }

  participantCanPublishMedia(_participant) {
    return true;
  }

  chatTransport() {
    const prefix = this.privatePrefix(this.joined.participant);
    if (this.roomState.chat_policy === "disabled") {
      return { subscribePatterns: [], publishChannel: undefined };
    }
    return { subscribePatterns: ["private.*.*.chat.room"], publishChannel: `${prefix}.chat.room` };
  }

  recordAudioDiagnostic(event) {
    const next = applyAudioDiagnostic(this.audioDiagnostics, event);
    this.audioDiagnostics = next;
    const now = Date.now();
    const pushUi = isSignificantDiagnosticEvent(event.type)
      || !this.audioDiagUiAt
      || now - this.audioDiagUiAt >= 500;
    if (pushUi) {
      this.audioDiagUiAt = now;
      this.ui.setAudioDiagnostics(next);
    }
    if (next.framePackets > 0 && next.playedFrames === 0 && (!this.audioMixer?.context || this.audioMixer.context.state !== "running")) {
      this.ui.setMediaNote("Remote audio packets are arriving; click anywhere once if the browser has not unlocked playback yet.");
    }
  }

  resetAudioDiagnostics() {
    this.audioDiagnostics = createAudioDiagnostics();
    this.ui.setAudioDiagnostics(this.audioDiagnostics);
  }

  armAudioPlayback() {
    if (this.audioUnlockHandler) return;
    this.audioUnlockHandler = async () => {
      try {
        if (await this.enableRemoteAudio()) this.disarmAudioPlayback();
      } catch (error) {
        this.ui.setMediaNote(`Audio playback unlock failed: ${friendlyError(error)}`);
      }
    };
    const options = { capture: true, passive: true };
    window.addEventListener("pointerdown", this.audioUnlockHandler, options);
    window.addEventListener("keydown", this.audioUnlockHandler, { capture: true });
    window.addEventListener("touchstart", this.audioUnlockHandler, options);
  }

  disarmAudioPlayback() {
    if (!this.audioUnlockHandler) return;
    const options = { capture: true };
    window.removeEventListener("pointerdown", this.audioUnlockHandler, options);
    window.removeEventListener("keydown", this.audioUnlockHandler, options);
    window.removeEventListener("touchstart", this.audioUnlockHandler, options);
    this.audioUnlockHandler = undefined;
  }
}

function friendlyError(error) {
  return error?.message || String(error);
}
